<script lang="ts">
export default {
  name: 'ExtractionReviewShell',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Loader, FileText } from '@lucide/vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PdfCanvas from '@/components/document/PdfCanvas.vue';
import ExtractedFieldsList from '@/components/document/ExtractedFieldsList.vue';
import { useFieldData } from '@/composables/useFieldData';
import { aiService } from '@/services/AIService';
import type { AIPromptResult, EntityMatch, ExtractionTarget, PolicySourceRestriction } from '@/types/ai';
import type { CoverageType } from '@/types/PolicyType';
import type { ParsedField } from '@/types/field';

// The document-and-fields half of an extraction review, shared by every surface that
// reviews one. What the review commits to is the host's business: this component renders
// the extraction, and the host slots in the action panel for its target.
const props = defineProps<{
  result: AIPromptResult;
  // Values an entity field may be corrected to. Only the surface that knows which
  // reference data applies supplies these; without them the fields stay free text.
  entityOptions?: Partial<Record<EntityMatch['entityType'], string[]>>;
  // The group each selectable value rolls up to, keyed by value.
  entityGroups?: Record<string, string>;
  // Whether the reference lists failed to load, so the fields can say so rather than
  // quietly accepting text the create will reject.
  entityOptionsFailed?: boolean;
}>();

const emit = defineEmits<{
  'review-completed': [];
  'target-resolved': [target: ExtractionTarget | null];
  // Also exposed as a slot prop for the action panel. A host that navigates between
  // extractions needs it outside the slot, to stop a reviewer walking away from edits.
  'pending-changes': [count: number];
}>();

const isLoadingDocument = ref(false);
const pdfUrl = ref<string | null>(null);
const overriddenFields = ref<string[]>([]);
const policySourceRestriction = ref<PolicySourceRestriction | null>(null);
const lineCoverageTypeOptions = ref<CoverageType[]>([]);
const entityMatches = ref<EntityMatch[]>([]);
const target = ref<ExtractionTarget | null>(null);
const pendingEditCount = ref(0);

// A resolved entity a reviewer has corrected, keyed by field path. Only corrected ones:
// an untouched field still holds the document's wording, while what gets created is the
// value the match resolved, so reading every field here would misreport the record.
const entityValues = computed<Record<string, string>>(() => {
  const values: Record<string, string> = {};
  for (const match of entityMatches.value) {
    if (!overriddenFields.value.includes(match.fieldPath)) {
      continue;
    }
    const field = fields.value.find((f) => f.path === match.fieldPath);
    if (field?.value !== undefined && field.value !== null) {
      values[match.fieldPath] = String(field.value);
    }
  }
  return values;
});

const { fields, selectedField, fieldSelectionTrigger, error: fieldError, parseJsonString, selectField, clearFields } =
  useFieldData();

async function load() {
  isLoadingDocument.value = true;

  try {
    try {
      const mergedResult = await aiService.getMergedPromptResult(props.result.id);
      // The merged output carries persisted reviewer edits; raw output is the fallback
      // when the merge endpoint is unavailable.
      if (mergedResult.mergedOutput) {
        parseJsonString(mergedResult.mergedOutput);
      } else if (props.result.rawOutput) {
        parseJsonString(props.result.rawOutput);
      }
      overriddenFields.value = mergedResult.overriddenFields || [];
      policySourceRestriction.value = mergedResult.policySourceRestriction ?? null;
      lineCoverageTypeOptions.value = (mergedResult.lineCoverageTypeOptions ?? []).map((o) => ({
        coverageTypeId: o.id,
        coverageType: o.name,
        productName: '',
      }));
      entityMatches.value = mergedResult.entityMatches ?? [];
      target.value = mergedResult.target ?? null;
    } catch (mergeErr) {
      console.warn('Failed to fetch merged result, using rawOutput:', mergeErr);
      if (props.result.rawOutput) {
        parseJsonString(props.result.rawOutput);
      }
      overriddenFields.value = [];
      policySourceRestriction.value = null;
      lineCoverageTypeOptions.value = [];
      entityMatches.value = [];
      target.value = null;
    }
    emit('target-resolved', target.value);

    // Prefer the platform document over an AI playground upload.
    if (props.result.documentUuid && props.result.documentAccountId) {
      const { documentService } = await import('@/services/v2/DocumentService');
      pdfUrl.value = await documentService.downloadDocumentFile(
        props.result.documentAccountId,
        props.result.documentUuid,
        true,
      );
    } else if (props.result.documentArn) {
      pdfUrl.value = await aiService.downloadDocument(props.result.documentArn, true);
    } else {
      pdfUrl.value = null;
    }
  } catch (err) {
    console.error('Failed to load document:', err);
    pdfUrl.value = null;
  } finally {
    isLoadingDocument.value = false;
  }
}

function handleFieldSelected(field: ParsedField) {
  selectField(field);
}

function handlePendingChanges(count: number) {
  pendingEditCount.value = count;
  emit('pending-changes', count);
}

watch(
  () => props.result?.id,
  (id) => {
    if (id) {
      load();
    } else {
      pdfUrl.value = null;
      clearFields();
    }
  },
  { immediate: true },
);

defineExpose({ reload: load, target, entityMatches });
</script>

<template>
  <div class="grid min-h-0 flex-1 grid-cols-3 gap-4 overflow-hidden">
    <Card class="relative col-span-2 flex min-h-0 flex-col overflow-hidden border-slate-200 bg-white/80 backdrop-blur-sm">
      <div
        v-if="isLoadingDocument"
        class="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/90">
        <div class="flex flex-col items-center gap-2">
          <Loader class="h-8 w-8 animate-spin text-blue-600" />
          <span class="text-slate-600">Loading document...</span>
        </div>
      </div>

      <CardHeader class="shrink-0 pb-2">
        <CardTitle class="flex items-center gap-2 text-lg">
          <FileText class="h-5 w-5 text-green-600" />
          Document Viewer
        </CardTitle>
      </CardHeader>
      <CardContent class="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
        <PdfCanvas
          :pdf-url="pdfUrl"
          :selected-field="selectedField"
          :selection-trigger="fieldSelectionTrigger" />
      </CardContent>
    </Card>

    <div class="col-span-1 flex min-h-0 flex-col gap-3 overflow-hidden">
      <Card class="flex min-h-0 flex-1 flex-col overflow-hidden border-slate-200 bg-white/80 backdrop-blur-sm">
        <ExtractedFieldsList
          :fields="fields"
          :selected-field="selectedField"
          :error="fieldError"
          :raw-output="result.rawOutput"
          :prompt-result-id="result.id"
          :overridden-fields="overriddenFields"
          :coverage-types="lineCoverageTypeOptions"
          :policy-source-restriction="policySourceRestriction"
          :entity-matches="entityMatches"
          :entity-options="entityOptions"
          :entity-groups="entityGroups"
          :entity-options-failed="entityOptionsFailed"
          :target-kind="target?.kind"
          :malformed="result.isMalformed === true"
          @field-selected="handleFieldSelected"
          @overrides-changed="load"
          @review-completed="emit('review-completed')"
          @pending-changes="handlePendingChanges" />
      </Card>

      <!-- The commit surface for this extraction's target. -->
      <slot
        name="action-panel"
        :target="target"
        :entity-matches="entityMatches"
        :entity-values="entityValues"
        :pending-edit-count="pendingEditCount"
        :reload="load" />
    </div>
  </div>
</template>
